import { type CurrencyCode, getCurrencyScale } from './currency';
import type { Money } from './money';
import { Money as M } from './money';

export type ParseMoneyError =
  | { kind: 'empty' }
  | { kind: 'invalid-chars'; input: string }
  | { kind: 'ambiguous-separators'; input: string }
  | { kind: 'multiple-decimals'; input: string }
  | { kind: 'too-many-fraction-digits'; input: string; maxScale: number }
  | { kind: 'out-of-range'; input: string };

export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: ParseMoneyError };

export interface ParseMoneyOptions {
  /**
   * BCP 47 locale tag used to disambiguate thousand / decimal separators
   * (e.g. `"en-US"` → `"."` decimal, `","` grouping; `"de-DE"` → `","`
   * decimal, `"."` grouping). Defaults to `"en-US"`.
   */
  locale?: string;
}

interface LocaleSeparators {
  decimal: '.' | ',';
  /**
   * The grouping separator, when it is an ASCII `.` or `,`. For locales
   * that group with whitespace (fr-FR uses narrow NBSP, sv-SE uses
   * regular space) this is `null`; whitespace has already been stripped
   * upstream, so the scanner doesn't need to track it.
   */
  group: '.' | ',' | null;
}

const SEP_CACHE = new Map<string, LocaleSeparators>();

function detectSeparators(locale: string): LocaleSeparators {
  const cached = SEP_CACHE.get(locale);
  if (cached) return cached;
  let seps: LocaleSeparators = { decimal: '.', group: ',' };
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const decimalRaw = parts.find((p) => p.type === 'decimal')?.value ?? '.';
    const groupRaw = parts.find((p) => p.type === 'group')?.value ?? ',';
    const decimal: '.' | ',' =
      decimalRaw === '.' || decimalRaw === ',' ? decimalRaw : '.';
    const group: '.' | ',' | null =
      groupRaw === '.' || groupRaw === ',' ? groupRaw : null;
    seps = { decimal, group };
  } catch {
    /* fall through to defaults */
  }
  SEP_CACHE.set(locale, seps);
  return seps;
}

/**
 * Parse a free-text money input under a known `currency`, returning a
 * tagged `Result`. Locale-aware: `"1.234,56"` parses as `1234.56` in
 * `de-DE` but is rejected as `ambiguous-separators` in `en-US`.
 *
 * Disambiguation rule: the *last* `.` or `,` in the input must match the
 * locale's decimal separator. Any other occurrences of `.` or `,` that
 * match the locale's group separator are treated as grouping and
 * stripped. Any other configuration is an ambiguity error.
 *
 * Fraction digits beyond the currency's scale are rejected (no silent
 * rounding — callers must round explicitly via `Money.mul`/`div`).
 */
export function parseMoneyInput(
  raw: string,
  currency: CurrencyCode,
  options: ParseMoneyOptions = {},
): ParseResult<Money> {
  const locale = options.locale ?? 'en-US';
  const { decimal, group } = detectSeparators(locale);
  const scale = getCurrencyScale(currency);

  const stripped = raw.replace(/[\s\u00a0\u202f]+/g, '').trim();
  if (stripped === '') {
    return err({ kind: 'empty' });
  }

  const signMatch = /^[-+]/.exec(stripped);
  const sign = signMatch?.[0] === '-' ? '-' : '';
  const body = signMatch ? stripped.slice(signMatch[0].length) : stripped;

  if (body === '') {
    return err({ kind: 'invalid-chars', input: raw });
  }

  // Every char must be a digit, `.`, or `,`. Anything else is garbage.
  if (!/^[0-9.,]+$/.test(body)) {
    return err({ kind: 'invalid-chars', input: raw });
  }

  const lastDot = body.lastIndexOf('.');
  const lastComma = body.lastIndexOf(',');
  const lastSepIdx = Math.max(lastDot, lastComma);
  const lastSep: '.' | ',' | null = lastSepIdx < 0 ? null : (body[lastSepIdx] as '.' | ',');

  let whole: string;
  let frac: string;

  if (lastSep === null) {
    // Pure digits.
    whole = body;
    frac = '';
  } else if (lastSep === decimal) {
    const decimalCount = countChar(body, decimal);
    if (decimalCount > 1) {
      return err({ kind: 'multiple-decimals', input: raw });
    }
    const beforeDecimal = body.slice(0, lastSepIdx);
    const afterDecimal = body.slice(lastSepIdx + 1);
    /* v8 ignore next 3 -- defensive: `lastSepIdx` is the last separator
       so afterDecimal cannot contain `.` or `,`. */
    if (/[.,]/.test(afterDecimal)) {
      return err({ kind: 'invalid-chars', input: raw });
    }
    const otherSep = decimal === '.' ? ',' : '.';
    if (beforeDecimal.includes(otherSep) && otherSep !== group) {
      return err({ kind: 'ambiguous-separators', input: raw });
    }
    whole = group !== null ? beforeDecimal.split(group).join('') : beforeDecimal;
    /* v8 ignore next 3 -- defensive: char filter + group stripping make
       this unreachable, but we keep the guard in case the filter loosens. */
    if (!/^\d*$/.test(whole)) {
      return err({ kind: 'invalid-chars', input: raw });
    }
    frac = afterDecimal;
  } else {
    // The last separator is not the locale's decimal. If it matches the
    // locale's group separator and the input contains no other kind of
    // separator, it's still a grouped integer (`"1,234"` in en-US).
    // Everything else is ambiguous.
    if (group !== null && lastSep === group) {
      const otherSep = group === '.' ? ',' : '.';
      if (body.includes(otherSep)) {
        return err({ kind: 'ambiguous-separators', input: raw });
      }
      whole = body.split(group).join('');
      if (!/^\d+$/.test(whole)) {
        return err({ kind: 'invalid-chars', input: raw });
      }
      frac = '';
    } else {
      return err({ kind: 'ambiguous-separators', input: raw });
    }
  }

  if (whole === '' && frac === '') {
    return err({ kind: 'invalid-chars', input: raw });
  }

  if (frac.length > scale) {
    return err({ kind: 'too-many-fraction-digits', input: raw, maxScale: scale });
  }

  const normalised = `${sign}${whole || '0'}${frac ? `.${frac}` : ''}`;
  try {
    // `parseMoneyInput` IS the canonical "decimal string came from a
    // human" entry point — calling the deprecated `Money.of(string)`
    // overload here is the right thing to do (the deprecation guards
    // against ad-hoc callers using it for wire amounts, not against
    // the parser that owns the decimal-string contract).
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return ok(M.of(normalised, currency));
    /* v8 ignore next 3 -- defensive: `normalised` is always a well-formed
       decimal string, so `Money.of` cannot throw here. */
  } catch {
    return err({ kind: 'out-of-range', input: raw });
  }
}

function countChar(s: string, ch: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === ch) n++;
  }
  return n;
}

function ok<T>(value: T): ParseResult<T> {
  return { ok: true, value };
}
function err(error: ParseMoneyError): ParseResult<never> {
  return { ok: false, error };
}
